
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { JobActivityLog } from "@/types/activity";
import { Profile } from "@/types/candidate";

async function fetchJobActivityLog(jobId: string): Promise<JobActivityLog[]> {
  if (!jobId) {
    console.log("fetchJobActivityLog: jobId is undefined, returning empty array.");
    return [];
  }
  console.log(`fetchJobActivityLog: Fetching activity log for job ID: ${jobId}`);

  const { data, error } = await supabase
    .from("job_activity_log")
    .select(`
      id,
      job_id,
      user_id,
      activity_type,
      details,
      activity_timestamp,
      created_at,
      profiles!user_id ( 
        id,
        first_name,
        last_name,
        email
      )
    `)
    .eq("job_id", jobId)
    .order("activity_timestamp", { ascending: false });

  if (error) {
    console.error("Error fetching job activity log:", error.message, error); // Log the full error object
    // It's possible the error object has more details than just error.message
    throw new Error(`Failed to fetch job activity log: ${error.message}`);
  }

  console.log(`fetchJobActivityLog: Successfully fetched ${data?.length || 0} activity log items.`);
  
  // The data for profiles will be nested under 'profiles'.
  // We map it to 'user' property for consistency in JobActivityLog type.
  return (data || []).map(item => {
    // If item.profiles is an error object (which it shouldn't be now), treat user as null.
    // Supabase returns null for a left join if no match, or the object if a match.
    const userProfile = item.profiles && typeof item.profiles === 'object' && !('error' in item.profiles) 
      ? item.profiles as Pick<Profile, 'id' | 'first_name' | 'last_name' | 'email'> 
      : null;

    return {
      ...item,
      user: userProfile
    };
  }) as JobActivityLog[];
}

export function useJobActivityLog(jobId: string) {
  return useQuery<JobActivityLog[], Error>({
    queryKey: ["jobActivityLog", jobId],
    queryFn: () => fetchJobActivityLog(jobId),
    enabled: !!jobId,
    retry: 1,
  });
}
